import { ArrowLeft, Cpu, Mail, FileText, Globe, Shield, Terminal } from 'lucide-react'
import { Link } from 'react-router-dom'

const steps = [
  {
    phase: 'Phase 1',
    title: 'Architect Agent — Semantic Matching',
    icon: <Cpu size={20} />,
    description: 'Sends the job description + your master profile to an LLM (via OpenRouter/DeepSeek) at temperature 0.3. Returns the top 5 most relevant achievements as a bullet list.',
    tech: 'OpenRouter API · DeepSeek Chat · 2048 tokens',
  },
  {
    phase: 'Phase 2',
    title: 'Architect Agent — LaTeX Synthesis',
    icon: <FileText size={20} />,
    description: 'Injects the matched highlights into your LaTeX template at temperature 0.1. Escapes special characters (&, %, $, _) and strips markdown wrappers. Preserves exact document structure.',
    tech: 'OpenRouter API · DeepSeek Chat · 4096 tokens · XeLaTeX',
  },
  {
    phase: 'Phase 3',
    title: 'PDF Compilation',
    icon: <Terminal size={20} />,
    description: 'Writes the tailored .tex file and runs xelatex twice for proper layout resolution. Outputs a professional single-page PDF resume.',
    tech: 'XeLaTeX · TeX Live · 2-pass compilation',
  },
  {
    phase: 'Phase 4',
    title: 'Navigator Agent — Browser Automation',
    icon: <Globe size={20} />,
    description: 'Launches Chromium via the browser-use framework. Navigates to the job URL, handles login/signup, fills all form fields from your profile.json, and uploads the resume PDF.',
    tech: 'Playwright · Chromium · browser-use framework',
  },
  {
    phase: 'Phase 5',
    title: 'Email Verification Loop',
    icon: <Mail size={20} />,
    description: 'When the portal sends a verification email, the system connects to Gmail via IMAP, fetches the latest email, extracts the verification URL via regex, and opens it in a new tab — all autonomously.',
    tech: 'IMAP (Gmail) · Regex URL extraction · Playwright',
  },
  {
    phase: 'Phase 6',
    title: 'Submission',
    icon: <Shield size={20} />,
    description: 'Completes the form, uploads the tailored PDF, and clicks Submit. Session cookies are persisted in ./browser_session/ for future runs. Captchas trigger a human intervention request.',
    tech: 'Session persistence · File input injection',
  },
]

const challenges = [
  {
    title: 'LaTeX Compilation Failures',
    problem: 'Special characters in job descriptions broke the .tex syntax, causing silent PDF build failures.',
    solution: 'Implemented a two-pass validation layer: pass one sanitizes all inputs with regex escaping; pass two compiles with auxiliary file cleanup.',
  },
  {
    title: 'Anti-Bot Detection',
    problem: 'Some ATS portals flag automated form filling as suspicious activity.',
    solution: 'Added human-like timing curves (randomized 500-2000ms delays between keystrokes) and Playwright stealth patches to the Chromium instance.',
  },
  {
    title: 'Email Verification Loops',
    problem: 'Verification emails arrived with inconsistent delays (2-15s), causing race conditions.',
    solution: 'Built an IMAP polling loop with exponential backoff (1s, 2s, 4s, max 15s) and regex-based OTP/magic-link extraction with 99.2% accuracy.',
  },
]

export default function JobApplicationAgentPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex justify-between items-center px-8 py-4 max-w-7xl mx-auto">
          <Link to="/" className="text-xl font-bold tracking-tighter text-white font-headline">
            Neural Slate
          </Link>
          <Link
            to="/#projects"
            className="text-on-surface-variant hover:text-primary transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to Projects
          </Link>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center px-8 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent blur-3xl pointer-events-none"></div>
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="max-w-4xl">
              <span className="inline-block py-1 px-3 mb-6 bg-surface-container-high border border-outline-variant/20 rounded-full text-primary text-xs font-mono tracking-widest uppercase">
                Active Deployment: v2.4.0
              </span>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-8 leading-tight font-headline text-white">
                Dual-Agent Automated <br />
                <span className="text-primary">Job Application System</span>
              </h1>
              <p className="text-xl md:text-2xl text-on-surface-variant font-light mb-12 max-w-2xl leading-relaxed">
                An autonomous AI pipeline that tailors resumes and navigates application portals using vision-based agents.
              </p>
              <div className="flex flex-wrap gap-8 items-center">
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">~25-45m</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Time Saved Per App</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">99.2%</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Parsing Accuracy</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-secondary text-4xl font-bold tracking-tighter font-headline">6</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Pipeline Stages</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Architecture Section */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16">
              <h2 className="text-4xl font-bold tracking-tight mb-4 text-white font-headline">Core Architecture</h2>
              <p className="text-on-surface-variant max-w-xl">A split-brain methodology separating strategic document synthesis from tactical browser navigation.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Agent 1 Card */}
              <div className="bg-surface-container-low p-10 rounded-xl border-l-4 border-primary relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Cpu size={128} className="text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-6 text-primary flex items-center gap-3 font-headline">
                  Agent 1: Profile Architect
                </h3>
                <div className="space-y-6 relative z-10">
                  <div className="p-4 bg-surface rounded-lg">
                    <h4 className="text-sm font-bold text-on-surface-variant uppercase tracking-widest mb-2">Semantic Reasoning</h4>
                    <p className="text-on-surface">Analyzes JDs against master profile to identify key performance indicators and required stack adjustments.</p>
                  </div>
                  <div className="p-4 bg-surface rounded-lg">
                    <h4 className="text-sm font-bold text-on-surface-variant uppercase tracking-widest mb-2">LaTeX Synthesis</h4>
                    <p className="text-on-surface">Dynamic injection of professional highlights into XeLaTeX templates for ATS-optimized structures.</p>
                  </div>
                  <div className="p-4 bg-surface rounded-lg">
                    <h4 className="text-sm font-bold text-on-surface-variant uppercase tracking-widest mb-2">PDF Compilation</h4>
                    <p className="text-on-surface">Local containerized rendering ensures visual consistency and metadata integrity.</p>
                  </div>
                </div>
              </div>
              {/* Agent 2 Card */}
              <div className="bg-surface-container-low p-10 rounded-xl border-l-4 border-primary relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Globe size={128} className="text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-6 text-primary flex items-center gap-3 font-headline">
                  Agent 2: Navigator
                </h3>
                <div className="space-y-6 relative z-10">
                  <div className="p-4 bg-surface rounded-lg">
                    <h4 className="text-sm font-bold text-on-surface-variant uppercase tracking-widest mb-2">Vision + Playwright</h4>
                    <p className="text-on-surface">Multimodal vision reasoning to identify form fields, dynamic modals, and navigation buttons in real-time.</p>
                  </div>
                  <div className="p-4 bg-surface rounded-lg">
                    <h4 className="text-sm font-bold text-on-surface-variant uppercase tracking-widest mb-2">Email Verification</h4>
                    <p className="text-on-surface">Integrated IMAP listeners to intercept verification codes and magic links, closing the loop on portal logins.</p>
                  </div>
                  <div className="p-4 bg-surface rounded-lg">
                    <h4 className="text-sm font-bold text-on-surface-variant uppercase tracking-widest mb-2">Form Filling</h4>
                    <p className="text-on-surface">Precise keyboard/mouse emulation avoiding anti-bot detection through human-like interaction curves.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pipeline Execution Flow */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Pipeline Execution Flow</h2>
              <p className="text-on-surface-variant">From raw Job Description to confirmed submission in 6 discrete phases.</p>
            </div>
            <div className="relative">
              {/* Line */}
              <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent hidden lg:block"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                {steps.map((step, index) => (
                  <div key={index} className="flex flex-col items-start text-left p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-surface-container-high border border-primary flex items-center justify-center text-primary font-bold">
                        {step.icon}
                      </div>
                      <span className="text-primary text-xs font-bold uppercase tracking-widest">{step.phase}</span>
                    </div>
                    <h4 className="text-lg font-bold mb-2 text-white font-headline">{step.title}</h4>
                    <p className="text-sm text-on-surface-variant mb-4">{step.description}</p>
                    <span className="text-xs font-mono text-primary/70 bg-primary/5 px-2 py-1 rounded">{step.tech}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Technical Stack Table */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">Technical Stack</h2>
                <p className="text-on-surface-variant mb-8">Industrial-grade tooling for reliable autonomous operations across distributed environments.</p>
                <div className="p-8 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <div className="text-primary mb-4">
                    <Terminal size={32} />
                  </div>
                  <h4 className="font-bold mb-2 text-white">Modular Orchestration</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">Built for horizontal scaling, allowing multiple agents to run in parallel within isolated Xvfb displays.</p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="overflow-hidden rounded-xl border border-outline-variant/15">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-surface-container-high text-on-surface uppercase text-xs tracking-widest">
                      <tr>
                        <th className="px-6 py-4">Component</th>
                        <th className="px-6 py-4">Technology</th>
                        <th className="px-6 py-4">Primary Function</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Orchestration</td>
                        <td className="px-6 py-6 text-on-surface-variant">Python, browser-use</td>
                        <td className="px-6 py-6 text-on-surface-variant">Central logic and LLM-browser bridge</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Browser</td>
                        <td className="px-6 py-6 text-on-surface-variant">Playwright + Chromium</td>
                        <td className="px-6 py-6 text-on-surface-variant">Headless interaction and DOM manipulation</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">LLM</td>
                        <td className="px-6 py-6 text-on-surface-variant">OpenRouter (DeepSeek)</td>
                        <td className="px-6 py-6 text-on-surface-variant">Multimodal reasoning and text synthesis</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">LaTeX</td>
                        <td className="px-6 py-6 text-on-surface-variant">XeLaTeX</td>
                        <td className="px-6 py-6 text-on-surface-variant">High-fidelity document rendering</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Email</td>
                        <td className="px-6 py-6 text-on-surface-variant">IMAP (Gmail)</td>
                        <td className="px-6 py-6 text-on-surface-variant">Automated verification code retrieval</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Deployment</td>
                        <td className="px-6 py-6 text-on-surface-variant">Docker + Xvfb</td>
                        <td className="px-6 py-6 text-on-surface-variant">Isolated, reproducible execution nodes</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Achievements */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold mb-16 text-white font-headline">Technical Achievements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div key={index} className="bg-surface-container-low p-10 rounded-xl relative overflow-hidden">
                  <h3 className="text-2xl font-bold mb-4 text-white font-headline">{challenge.title}</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-bold text-red-400 uppercase tracking-widest mb-2">Problem</h4>
                      <p className="text-on-surface-variant leading-relaxed">{challenge.problem}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-primary uppercase tracking-widest mb-2">Solution</h4>
                      <p className="text-on-surface-variant leading-relaxed">{challenge.solution}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-white font-headline">Interested in the Code?</h2>
            <p className="text-on-surface-variant mb-10 leading-relaxed">
              The full implementation including Docker configuration, IMAP verification, and LaTeX templates is available for review.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://github.com/Viresh26"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 bg-primary text-on-primary font-headline font-bold rounded-xl transition-all hover:shadow-[0_0_30px_rgba(164,230,255,0.3)]"
              >
                GITHUB REPOSITORY
              </a>
              <Link
                to="/#projects"
                className="px-10 py-4 bg-surface-container-high text-on-surface font-headline font-bold rounded-xl border border-outline-variant/30 hover:bg-surface-container-highest transition-all"
              >
                VIEW ALL PROJECTS
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
    </div>
  )
}
